import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardComponent } from './dashboard/dashboard.component';
import { MarketplaceComponent } from './marketplace/marketplace.component';
import { AsamcoRouterGuard, AsamcoUserService, CustomRouteConfig, Permissions } from '@asamco/base';
import { LinkEditPageComponent } from './link/link-edit-page/link-edit-page.component';
import { UsermanagementComponent } from './usermanagement/usermanagement.component';
import { FunctionappmanagementComponent } from './functionappmanagement/functionappmanagement.component';
import { ContactComponent } from './contact/contact.component';
import { FunctionappversioningComponent } from './functionappversioning/functionappversioning.component';
import { AdminLinkDisplayComponent } from './admin-link-overview/admin-link-display/admin-link-display.component';
import { AdminAuditLogsOverviewComponent } from './admin-link-overview/admin-audit-logs-overview/admin-audit-logs-overview.component';
import { AdminFinancialIssuesOverviewComponent } from './admin-link-overview/admin-financial-issues-overview/admin-financial-issues-overview.component';
import { AdminQueueLinkOverviewComponent } from './admin-link-overview/admin-queue-link-overview/admin-queue-link-overview.component';
import { AdminLinkMaintenanceComponent } from './admin-link-overview/admin-link-maintenance/admin-link-maintenance.component';
import { ApplicationLogsOverviewComponent } from './application-logs-overview/application-logs-overview.component';
import { AdminToolingOverviewComponent } from './admin-tooling/admin-tooling-overview/admin-tooling-overview.component';
import { ApplicationEditComponent } from './admin-tooling/tools/application/application-edit/application-edit.component';
import { FunctionAppsComponent } from './function-apps/function-apps.component';
import { DurableFunctionAppManagementComponent } from './durable-function-app-management/durable-function-app-management.component';
import { EulaComponent } from './eula/eula.component';
import { AdminCompanyManagementGridComponent } from './admin-tooling/tools/admin-company-management-grid/admin-company-management-grid.component';
import { AdminCompanyRequestManagementGridComponent } from './admin-tooling/tools/admin-company-request-management-grid/admin-company-request-management-grid.component';
import { AdminCompanyAccessRequestManagementGridComponent } from './admin-tooling/tools/admin-company-access-request-management-grid/admin-company-access-request-management-grid.component';
import { AdminUserCompanyManagementGridComponent } from './admin-tooling/tools/admin-user-company-management-grid/admin-user-company-management-grid.component';
import { AdminUsersManagementGridComponent } from './admin-tooling/tools/admin-users-management-grid/admin-users-management-grid.component';
import { AdminGroupsManagementGridComponent } from './admin-tooling/tools/admin-groups-management-grid/admin-groups-management-grid.component';
import { AdminUserPermissionsPageComponent } from './admin-tooling/tools/admin-user-permissions-page/admin-user-permissions-page.component';
import { ApplicationsComponent } from './admin-tooling/tools/application/applications/applications.component';
import { LinkJoblogAdminShellComponent } from './link/link-joblog-searcher-admin/link-joblog-admin-shell/link-joblog-admin-shell.component';
import { LinkJoblogPatternsAdminComponent } from './link/link-joblog-searcher-admin/link-joblog-patterns-admin/link-joblog-patterns-admin.component';
import { LinkJoblogSearchAdminComponent } from './link/link-joblog-searcher-admin/link-joblog-search-admin/link-joblog-search-admin.component';
import { LinkJoblogUnmatchedAdmin } from './link/link-joblog-searcher-admin/link-joblog-unmatched-admin/link-joblog-unmatched-admin.component';
import { LinkJoblogReportAdminComponent } from './link/link-joblog-searcher-admin/link-joblog-report-admin/link-joblog-report-admin.component';
import { LinkTransferToolComponent } from './link-transfer-tool/link-transfer-tool.component';
import { IntegrationTesterShellComponent } from './integration-tester/integration-tester-shell.component';
import { MockDataToolComponent } from './mock-data-tool/mock-data-tool.component';
import { AdminChangelogManagementComponent } from './admin-tooling/tools/admin-changelog-management/admin-changelog-management.component';
import { WarningModalMessageToolComponent } from './warning-modal-message-tool/warning-modal-message-tool.component';
import { GroupDetailsComponent } from './user-management-shell/group-details/group-details.component';

type AssignedPermissionKey = keyof typeof Permissions;

const hasAssignedPermission = async (asamcoUserService: AsamcoUserService, permission: AssignedPermissionKey): Promise<boolean> => {
	const permissionGuid = Permissions[permission]?.toLowerCase();
	if (!permissionGuid) {
		return false;
	}

	const assignedPermissions = await asamcoUserService.getPermissions();
	return assignedPermissions.includes(permissionGuid);
};

const requireAssignedPermission = (permission: AssignedPermissionKey) => async () => {
	const asamcoUserService = inject(AsamcoUserService);
	const router = inject(Router);
	return (await hasAssignedPermission(asamcoUserService, permission)) ? true : router.createUrlTree(['/application-link/dashboard']);
};

const requireAnyAssignedPermission = (permissions: AssignedPermissionKey[]) => async () => {
	const asamcoUserService = inject(AsamcoUserService);
	const router = inject(Router);
	for (const permission of permissions) {
		if (await hasAssignedPermission(asamcoUserService, permission)) {
			return true;
		}
	}

	return router.createUrlTree(['/application-link/dashboard']);
};

const adminToolingAccessPermissions: AssignedPermissionKey[] = [
	'AL3_FUNC_APP',
	'AL3_COMPANY_VIEW',
	'AL3_USERCOMPANY_VIEW',
	'AL3_COMPANY_REQUESTS_HANDLE',
	'AL3_COMPANY_ACCESS_REQUESTS_HANDLE',
	'AS_UM_MI_USERS',
	'AS_UM_MI_GROUPS',
	'AL3_ADMIN_LINK_OVERVIEW',
	'AL3_MAINTENANCE_VIEW',
	'AL3_APPLICATION_MANAGEMENT',
	'AP_VIEW_APPLICATION_LOGS',
	'AL3_LINK_TRANSFER',
	'AL3_MOCK_DATA',
	'AL3_WARNING_MODAL_MESSAGES',
	'AL3_JOBLOG_PATTERN_MATCHER',
	'AL3_CHANGELOG',
	'AL3_TICKET_INTEGRATION',
	'AL3_INT_TEST',
];

export const routes: CustomRouteConfig = [
	{
		path: 'dashboard',
		component: DashboardComponent,
		canActivate: [AsamcoRouterGuard],
	},
	{
		path: 'marketplace',
		component: MarketplaceComponent,
		canActivate: [AsamcoRouterGuard],
		permission: 'AL3_MARKETPLACE',
	},
	{
		path: 'companyusermanagement',
		component: UsermanagementComponent,
		canActivate: [AsamcoRouterGuard],
	},
	{
		path: 'companyusermanagement/:tab',
		component: UsermanagementComponent,
		canActivate: [AsamcoRouterGuard],
	},
	{
		path: 'functionappmanagement',
		component: FunctionappmanagementComponent,
		canActivate: [AsamcoRouterGuard],
	},
	{
		path: 'durable-function-app-management',
		redirectTo: 'admin-tooling-overview/durable-function-app-management',
		pathMatch: 'full',
	},
	{
		path: 'functionappversioning',
		component: FunctionappversioningComponent,
		canActivate: [AsamcoRouterGuard],
	},
	{
		path: 'function-apps',
		redirectTo: 'admin-tooling-overview/function-apps',
		pathMatch: 'full',
	},
	{
		path: 'adminlinkoverview',
		redirectTo: 'admin-tooling-overview/link-overview',
		pathMatch: 'full',
	},
	{
		path: 'adminlinkoverview/:tab',
		redirectTo: 'admin-tooling-overview/:tab',
	},
	{
		path: 'admin-tooling-overview',
		component: AdminToolingOverviewComponent,
		canActivate: [AsamcoRouterGuard, requireAnyAssignedPermission(adminToolingAccessPermissions)],
		children: [
			{
				path: 'durable-function-app-management',
				component: DurableFunctionAppManagementComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AL3_FUNC_APP',
			},
			{ path: 'function-apps', component: FunctionAppsComponent, canActivate: [AsamcoRouterGuard], permission: 'AL3_FUNC_APP' },
			{
				path: 'ticket-integration',
				loadChildren: () => import('@asamco/ticketintegration/feature').then(m => m.routes),
				canActivate: [requireAssignedPermission('AL3_TICKET_INTEGRATION')],
			},
			{
				path: 'integrationtester',
				component: IntegrationTesterShellComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AL3_INT_TEST',
			},
			{
				path: 'company-management',
				component: AdminCompanyManagementGridComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AL3_COMPANY_VIEW',
			},
			{
				path: 'user-company-management',
				component: AdminUserCompanyManagementGridComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AL3_USERCOMPANY_VIEW',
			},
			{
				path: 'company-request-management',
				component: AdminCompanyRequestManagementGridComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AL3_COMPANY_REQUESTS_HANDLE',
			},
			{ path: 'user-management', redirectTo: 'users', pathMatch: 'full' },
			{ path: 'group-management', redirectTo: 'groups', pathMatch: 'full' },
			{
				path: 'users/:id/permissions',
				component: AdminUserPermissionsPageComponent,
				canActivate: [requireAssignedPermission('AS_UM_MI_USERS')],
			},
			{ path: 'users', component: AdminUsersManagementGridComponent, canActivate: [requireAssignedPermission('AS_UM_MI_USERS')] },
			{ path: 'groups', component: AdminGroupsManagementGridComponent, canActivate: [requireAssignedPermission('AS_UM_MI_GROUPS')] },
			{ path: 'groups/:id', component: GroupDetailsComponent, canActivate: [requireAssignedPermission('AS_UM_MI_GROUPS')] },
			{
				path: 'company-access-request-management',
				component: AdminCompanyAccessRequestManagementGridComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AL3_COMPANY_ACCESS_REQUESTS_HANDLE',
			},
			{
				path: 'link-overview',
				component: AdminLinkDisplayComponent,
				canActivate: [requireAssignedPermission('AL3_ADMIN_LINK_OVERVIEW')],
			},
			{
				path: 'audit-logs',
				component: AdminAuditLogsOverviewComponent,
				canActivate: [requireAssignedPermission('AL3_ADMIN_LINK_OVERVIEW')],
			},
			{
				path: 'financial-integration-issues',
				component: AdminFinancialIssuesOverviewComponent,
				canActivate: [requireAssignedPermission('AL3_ADMIN_LINK_OVERVIEW')],
			},
			{
				path: 'scheduler-data',
				component: AdminQueueLinkOverviewComponent,
				canActivate: [requireAssignedPermission('AL3_ADMIN_LINK_OVERVIEW')],
			},
			{
				path: 'maintenance',
				component: AdminLinkMaintenanceComponent,
				canActivate: [requireAssignedPermission('AL3_MAINTENANCE_VIEW')],
			},
			{
				path: 'link-transfer',
				component: LinkTransferToolComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AL3_LINK_TRANSFER',
			},
			{ path: 'mock-data', component: MockDataToolComponent, canActivate: [AsamcoRouterGuard], permission: 'AL3_MOCK_DATA' },
			{
				path: 'warning-modal-messages',
				component: WarningModalMessageToolComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AL3_WARNING_MODAL_MESSAGES',
			},
			{
				path: 'applications',
				component: ApplicationsComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AL3_APPLICATION_MANAGEMENT',
			},
			{
				path: 'application-logs',
				component: ApplicationLogsOverviewComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AP_VIEW_APPLICATION_LOGS',
			},
			{
				path: 'changelog-management',
				component: AdminChangelogManagementComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AL3_CHANGELOG',
			},

			{
				path: 'joblog-pattern-matcher',
				component: LinkJoblogAdminShellComponent,
				canActivate: [AsamcoRouterGuard],
				permission: 'AL3_JOBLOG_PATTERN_MATCHER',
				children: [
					{ path: '', pathMatch: 'full', redirectTo: 'analysis' },
					{ path: 'analysis', component: LinkJoblogSearchAdminComponent },
					{ path: 'patterns', component: LinkJoblogPatternsAdminComponent },
					{ path: 'unmatched', component: LinkJoblogUnmatchedAdmin },
					{ path: 'reporting', component: LinkJoblogReportAdminComponent },
				],
			},
		],
	},
	{
		path: 'link/:id',
		component: LinkEditPageComponent,
		canActivate: [AsamcoRouterGuard],
	},
	{
		path: 'application/:id',
		component: ApplicationEditComponent,
		canActivate: [AsamcoRouterGuard],
		permission: 'AL3_APPLICATION_MANAGEMENT',
	},
	{
		path: 'integrationtester',
		redirectTo: 'admin-tooling-overview/integrationtester',
		pathMatch: 'full',
	},
	{
		path: 'contact',
		component: ContactComponent,
		canActivate: [AsamcoRouterGuard],
	},
	{
		path: 'eula',
		component: EulaComponent,
		canActivate: [AsamcoRouterGuard],
	},
	{ path: '', redirectTo: 'dashboard', pathMatch: 'full' },
];
